import React, { useCallback, useRef } from 'react';
import { SideNavLinkProps, updateActiveCategory } from '../../store/slice';
import {
  IconAnchor,
  IconButton,
  SvgWrapper,
  tooltipThemeOverride,
} from './styles';
import { connect } from 'react-redux';
import { Text, ThemeProvider, Tooltip } from '@amzn/storm-ui';
import { gray50, white } from 'src/colors';
import { SvgIcon } from './SvgIcon';
import { NavMetricPublisher } from 'src/commons/utils/metrics';

interface ToolbarIconProps {
  id: string;
  iconPath: string;
  toolTip: string;
  isSelected?: boolean;
  isSingleSidebar?: boolean;
  singleSidebarApp?: SideNavLinkProps;
  activeCategory: string;
  clickToolbarIconHandler: (...args: any[]) => any;
  onIconClick?: (...args: any[]) => any;
}

export const ToolbarIcon: React.FC<ToolbarIconProps> = ({
  id,
  iconPath,
  toolTip,
  isSingleSidebar,
  singleSidebarApp,
  activeCategory,
  isSelected,
  clickToolbarIconHandler,
  onIconClick,
}) => {
  const svgContainer = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const onClickEvent = useCallback(() => {
    NavMetricPublisher.publishCounterMonitor(`toolbarItemClicked-${id}`, 1);

    if (isSingleSidebar) {
      return;
    }

    // if isSingleSidebar then link to
    if (onIconClick) {
      onIconClick();
    }

    clickToolbarIconHandler(activeCategory !== id ? id : '');
    buttonRef.current?.blur();
  }, [
    onIconClick,
    clickToolbarIconHandler,
    activeCategory,
    id,
    isSingleSidebar,
  ]);

  const IconWrapperComponent = (
    isSingleSidebar ? IconAnchor : IconButton
  ) as any;

  const toolBarIcon = (
    <IconWrapperComponent
      onClick={onClickEvent}
      href={isSingleSidebar ? singleSidebarApp?.link.url : undefined}
      autoFocus={activeCategory == id}
      data-takt-id={isSingleSidebar ? singleSidebarApp?.id : id}
      data-takt-value={isSingleSidebar ? `{"toolbarIcon":"${id}"}` : undefined}
      data-ccx-e2e-id={isSingleSidebar ? singleSidebarApp?.id : undefined}
      isSelected={isSelected}
      aria-label={toolTip}
      aria-haspopup={isSingleSidebar ? '' : true}
      aria-expanded={isSingleSidebar ? '' : activeCategory === id}
      ref={buttonRef}
    >
      <SvgWrapper
        data-ccx-e2e-id={id}
        ref={svgContainer}
        onMouseEnter={() => handleSvgMouseEnter(svgContainer, isSelected)}
        onMouseLeave={() => handleSvgMouseLeave(svgContainer, isSelected)}
      >
        <SvgIcon
          isSelected={isSelected}
          iconPath={iconPath}
          ariaLabel={toolTip}
        />
      </SvgWrapper>
    </IconWrapperComponent>
  );

  return (
    <ThemeProvider theme={tooltipThemeOverride}>
      <Tooltip
        nonInteractive
        trigger={toolBarIcon}
        withCloseButton={false}
        spacing={12}
        type="dark"
        position="right"
      >
        <Text fontSize="small">{toolTip}</Text>
      </Tooltip>
    </ThemeProvider>
  );
};

const handleSvgMouseEnter = (
  svgContainer: React.RefObject<HTMLDivElement>,
  isSelected?: boolean,
) => {
  if (!isSelected) {
    svgContainer.current
      ?.querySelectorAll('path')
      ?.forEach((node) => node.setAttribute('fill', white));
  }
};

const handleSvgMouseLeave = (
  svgContainer: React.RefObject<HTMLDivElement>,
  isSelected?: boolean,
) => {
  if (!isSelected) {
    svgContainer.current
      ?.querySelectorAll('path')
      ?.forEach((node) => node.setAttribute('fill', gray50));
  }
};

const mapDispatchToProps = (dispatch) => {
  return {
    clickToolbarIconHandler: (category) =>
      dispatch(updateActiveCategory(category)),
  };
};

export const ConnectedToolbarIcon = connect(
  null,
  mapDispatchToProps,
)(ToolbarIcon);
